/**
 * The dunning band (mission 9 WP13 T082; sheet 50 callout 10; B.1, NF.9; FR-007; U29; contracts/ui-api.md
 * "Sheet 50"). On sheet 50 it owns the message; the shell's banner is suppressed here (WP14).
 *
 * - `dunning`: a warning (`role="status"`), `dunningBanner` with the days left and the date read-only
 *   begins, and the fix action, Update card, to `#card`;
 * - `read-only-dunning`: blocked (`role="alert"`), `readOnlyBannerDunning`, Update card;
 * - `read-only-cancelled`: blocked, `readOnlyBannerCancelled` with the date the plan ended, Subscribe.
 *
 * Active renders nothing, and pending never reaches this page (U24). Never dismissible (§7.5). One
 * identity per tenant, so there is no Member "Ask an Owner" (C-007; 50/10's second part).
 */
import { Link } from 'react-router-dom'

import { Band } from '@/components/trellis/Band'
import type { BillingAccessView } from '@/lib/api/billing'
import { date } from '@/lib/format'
import { PROVISIONAL_COPY, copyText, type TextKey } from '@/lib/provisional-copy'

const CARD = '/app/settings/billing#card'

function Action({ to, label }: { to: string; label: TextKey }) {
  return (
    <Link to={to} className="text-body-sm font-medium text-[var(--color-link)] underline hover:text-[var(--color-link-hover)]">
      {copyText(label)}
    </Link>
  )
}

export function DunningBand({ access }: { access: BillingAccessView }) {
  if (access.state === 'dunning' && access.dunning_days_left !== null && access.read_only_at !== null) {
    return (
      <Band kind="warning" requirement="B.1" action={<Action to={CARD} label="cardUpdate" />}>
        <p>{PROVISIONAL_COPY.dunningBanner.render({ days: access.dunning_days_left, date: date(access.read_only_at) })}</p>
      </Band>
    )
  }
  if (access.state !== 'read_only') return null
  if (access.reason === 'dunning') {
    return (
      <Band kind="blocked" requirement="B.1" action={<Action to={CARD} label="readOnlyRemedyUpdate" />}>
        <p>{copyText('readOnlyBannerDunning')}</p>
      </Band>
    )
  }
  // With neither date the sentence cannot be said: nothing, as BillingBanner does (mission 9 review UI3).
  const ended = access.read_only_since ?? access.read_only_at
  if (ended === null) return null
  return (
    <Band kind="blocked" requirement="B.1" action={<Action to="/app/subscribe" label="readOnlyRemedySubscribe" />}>
      <p>{PROVISIONAL_COPY.readOnlyBannerCancelled.render({ date: date(ended) })}</p>
    </Band>
  )
}
